/**
 * Der Reiter "Anlieferung": je Bestellposition bestellte/gelieferte/offene Menge (direkt aus
 * bestellung.items - kommt bereits mit der Bestellung, kein eigener Fetch), aufklappbar zu den
 * einzelnen JTL-Wareneingangsbuchungen (Datum, Menge und einbuchender Benutzer, per Fetch beim
 * Oeffnen des Reiters geladen - siehe App.tsx, Reiter "Anlieferung").
 *
 * Aufklapp-Muster wie TimelinePage/ZollStufe.tsx: lokaler State-Toggle je Zeile, aria-expanded. Eine
 * Position OHNE Einbuchungen ist NICHT aufklappbar (kein Icon, keine Aktion) - Entscheidung aus der
 * Spec, Abschnitt "Entscheidungen".
 *
 * ================= Optische Nachbesserung (Auftrag "Anlieferungsliste huebscher machen", 2026-08-27) ====
 *
 * Der Aufklapp-Schalter war urspruenglich der GANZE Artikelname als klickbare Flaeche (wirkte wie ein
 * Link). Jetzt: ein eigenes kleines Icon-Symbol (`Ikone`, Kreis-Plus/-Minus) VOR dem Namen, der Name
 * selbst ist reiner Text. Zusaetzlich ein Artikel-Thumbnail (`Artikelbild`) vor dem Namen, wie in
 * `Drawer/CalcTab/PositionsTabelle.tsx` bereits gemacht. Die aufgeklappte Buchungsliste ist keine
 * `<ul>` mehr, sondern eine eigene kleine Tabelle (Datum · Menge · Eingebucht von).
 *
 * `Ikone` setzt `aria-hidden="true"` auf JEDES Symbol - das umschliessende `<button>` traegt deshalb
 * ein eigenes `aria-label` (Artikelname + Zustand), damit der Schalter weiterhin ueber seinen Namen
 * auffindbar bleibt.
 *
 * ================= `data-permission="view_board"` an der Tabelle (Fix Stufe 2, Important #1) ======
 *
 * `DRAWER_TAB_EDIT_PERMISSIONS.arrival = "edit_orders"` (`drawerRechte.ts`) sperrt ohne dieses
 * Attribut JEDES Bedienelement im Reiterrumpf fuer ein Konto ohne `edit_orders` - auch den
 * Aufklapp-Schalter (`button.arrival-position-schalter`), obwohl der NICHTS schreibt (er klappt nur
 * eine bereits geladene Liste auf/zu). Global Constraint 9 ("Lesen steht allen Rollen offen") und die
 * Design-Spec verlangen ausdruecklich, dass dieser Reiter fuer JEDE Rolle bedienbar bleibt.
 *
 * `view_board` ist eines der fuenf Leserechte (`state/rechteMatrix.ts#LESERECHTE`) und steht bei
 * JEDER der drei Systemrollen (`RECHTE_JE_ROLLE`) - anders als `edit_orders`, das der Rolle
 * "Zahlungsansicht" (`payment_manager`) fehlt. `data-permission` steht am AEUSSEREN `<table>`, nicht
 * am einzelnen Schalter: der Rechte-Durchgang des Rahmens sucht ueber `closest('[data-permission]')`
 * den naechsten Vorfahren mit dem Attribut (`Drawer.tsx`, Abschnitt "Der Rechte-Durchgang") - EIN
 * Attribut an der Tabelle deckt damit automatisch JEDEN Zeilen-Schalter ab, auch wenn eine Bestellung
 * mehrere aufklappbare Positionen traegt, ohne dass jede Zeile es einzeln tragen muesste. Muster:
 * `PaymentTab/Freigabe.tsx#data-permission="request_payment"` - dort steht es direkt am Knopf, weil
 * es dort GENAU einen gibt; hier steht es hoeher, weil es mehrere geben kann - jetzt eben ein
 * Icon-Button statt eines Textbuttons.
 *
 * ================= Artikelnummer-Spalte (Auftrag "Artikelnummer-Spalte", 2026-08-27) ================
 *
 * Eigene Spalte zwischen der Artikel-Zelle (Icon-Schalter + Thumbnail + Name) und "Bestellt": zeigt
 * `OrderItem.sku` (JTL `cArtNr`) je Position, Rueckfallstrich "–" ohne gesetzte Artikelnummer -
 * dieselbe Bauart wie die bereits vorhandene `bezeichnung`-Ableitung in derselben Funktion, kein neuer
 * Helfer. Vorbild fuer eine EIGENSTAENDIGE SKU-Spalte (getrennt von Name+Bild):
 * `Drawer/CalcTab/PositionsTabelle.tsx` (`<td className="ek-col-sku">`).
 *
 * ================= „Vollstaendig eingebucht"-Datum (Auftrag laut Rueckmeldung von Damian Gembries
 * auf Asana, 2026-08-27) ================
 *
 * WAS: sobald `vollstaendigGeliefert(bestellung)` zutrifft (ALLE Positionen `anzahlOffen <= 0`) UND
 * die Einbuchungen bereits geladen sind, erscheint zwischen Reiterleiste und Positionstabelle ein
 * gruener Hinweis "Vollstaendig eingebucht am {Datum}." - derselbe `.done-banner`-Stil wie in
 * `Drawer.tsx` fuer "Bestellung vollstaendig". WARUM: eine Bestellung mit lauter `anzahlOffen: 0`
 * kann trotzdem mehrere, zeitlich weit auseinanderliegende Einbuchungen tragen (z.B. eine vergessene,
 * spaeter nachgeholte Ersatzverpackung) - das Datum zeigt auf einen Blick, WANN die Bestellung
 * WIRKLICH fertig eingebucht wurde, ohne jede Position einzeln aufzuklappen. WARUM das SPAETESTE
 * Datum ueber ALLE Positionen und nicht das Datum der in `items` zuletzt stehenden Position: die
 * Reihenfolge in `items` ist eine Anzeige-Reihenfolge, keine zeitliche - die Position mit der
 * zeitlich letzten Buchung kann an beliebiger Stelle der Liste stehen.
 *
 * ================= Lieferstatus-Icons + Uhrzeit (Auftrag "Lieferstatus-Icons + Uhrzeit",
 * 2026-08-28) ================
 *
 * a) Je Position ein farbiger Kreis mit Symbol (`LieferAmpel`, `lieferStatusPosition` aus
 *    `lib/orderList.ts`): rot/`circle` = offen, gelb/`circle-dashed` = teilgeliefert,
 *    gruen/`circle-check-big` = komplett (`anzahlOffen <= 0`) - dieselbe `Number.isFinite`-
 *    Absicherung wie `vollstaendigGeliefert`.
 * b) Der bestehende gruene "Vollstaendig eingebucht"-Banner (oben) bleibt UNVERAENDERT. Zwei neue
 *    Geschwister-Banner im selben `.done-banner`-Stil fuer die beiden anderen Gesamtzustaende (nur
 *    wenn `einbuchungen !== null`): rot "Noch keine Einbuchung erfolgt.", wenn keine Position eine
 *    gelieferte Menge > 0 hat; gelb "Teilweise eingebucht - X von Y Positionen vollstaendig
 *    geliefert." sonst.
 * c) Die aufgeklappte Buchungstabelle zeigt zusaetzlich zum Datum die Uhrzeit der Einbuchung
 *    (`zeitVon`/`zeitBis`, `MIN`/`MAX(dGeliefertAM)` je Tagesgruppe, `backend/src/jtl/
 *    einbuchungen.ts`) - `einbuchungsZeitpunkt` unten.
 *
 * ================= Nachbesserung: eigene Spalte statt inline vor "Bestellt" (Auftrag
 * "Lieferstatus-Icon eigene Spalte", 2026-08-28) ================
 *
 * Nutzer-Feedback aus der Produktion: das Icon stand urspruenglich INLINE VOR der Bestellmenge in
 * derselben `td.mono`-Zelle - weil die Zahlen unterschiedlich breit sind ("2.330" vs. "70"), sprang
 * das Icon von Zeile zu Zeile sichtbar hin und her. Jetzt eine EIGENE Spalte ("Status") zwischen
 * "Bestellt" und "Geliefert", `.liefer-status-spalte` mit fester Breite (`styles/neubau/
 * anlieferung.css`) - die Icon-Position ist damit fuer jede Zeile identisch, unabhaengig von der
 * Ziffernzahl der Nachbarspalten. `colSpan` der aufgeklappten Buchungszeile entsprechend von 5 auf 6
 * erhoeht (eine Spalte mehr in der aeusseren Tabelle).
 */
import { useState } from "react";
import { fmtDate, NUM } from "../../../lib/format.ts";
import type { Order, OrderItem } from "../../../lib/types.ts";
import type { EinbuchungenJePosition, JtlEinbuchung } from "../../../api/einbuchungen.ts";
import {
  hatGelieferteMenge,
  lieferStatusPosition,
  vollstaendigGeliefert,
  type LieferStatus,
} from "../../../lib/orderList.ts";
import { Ikone } from "../../Ikone.tsx";
import { Artikelbild } from "../../Artikelbild.tsx";

export interface AnlieferungslisteProps {
  bestellung: Order;
  /**
   * `null` heisst: noch nicht geladen ODER der Ladeversuch ist ENDGUELTIG gescheitert - `fehlgeschlagen`
   * unterscheidet die beiden (siehe App.tsx, `anlieferungZustand`).
   */
  einbuchungen: EinbuchungenJePosition[] | null;
  /**
   * `true` NUR bei einem endgueltig gescheiterten Abruf fuer DIESE Bestellung - siehe App.tsx,
   * Important #2 (Fix Stufe 2). Bleibt `einbuchungen === null` mit `fehlgeschlagen: false`, ist der
   * Abruf entweder noch nicht gestartet, laeuft noch, oder die Bestellung hat keine gueltige
   * `jtlOrderId` - in allen drei Faellen zeigt der Reiter denselben "noch nicht geladen"-Hinweis.
   */
  fehlgeschlagen: boolean;
}

/**
 * Zeigt eine Menge oder den Rueckfall-Strich, wenn JTL sie nicht liefern konnte (`null`) - `null`
 * bedeutet "unbekannt", NICHT "0 Stueck" (Important #3, Fix Stufe 2). `?? 0` deutete `null` bisher als
 * Zahl 0 und erzeugte damit widerspruechliche Anzeigen ("Bestellt 100 · Geliefert 0 · Offen 0").
 * Dieselbe `Number.isFinite`-Ableitung wie `orderList.ts#vollstaendigGeliefert`, nur als Text statt
 * als Bedingung.
 */
function mengeText(wert: unknown): string {
  const n = Number(wert ?? NaN);
  return Number.isFinite(n) ? NUM.format(n) : "–";
}

/**
 * Symbol je Lieferstatus (Auftrag "Lieferstatus-Icons + Uhrzeit", 2026-08-28): `circle-check-big`
 * fuer "komplett" - bewusst dasselbe Symbol wie der bestehende gruene "Vollstaendig eingebucht"-
 * Banner weiter unten in dieser Datei; `circle` fuer "offen" (leerer Kreis, Gegenstueck dazu);
 * `circle-dashed` fuer "teilgeliefert" (gestrichelter Kreis, dazwischen) - neu in der `Ikone`-
 * Registratur, siehe `components/Ikone.tsx`.
 */
const LIEFER_STATUS_ICON: Record<LieferStatus, string> = {
  offen: "circle",
  teilgeliefert: "circle-dashed",
  komplett: "circle-check-big",
};

/**
 * Ein farbiger Kreis mit Symbol, in der EIGENEN Spalte "Status" (`td.liefer-status-spalte`)
 * zwischen "Bestellt" und "Geliefert" - NICHT mehr inline vor der Bestellmenge (Nachbesserung
 * "Lieferstatus-Icon eigene Spalte", 2026-08-28: dort sprang das Icon je nach Ziffernzahl der
 * Nachbarzahl sichtbar hin und her). Die Farben (Hintergrund je `.liefer-status-icon.<status>`,
 * Symbolfarbe ueber `color`/`currentColor`) stehen in `styles/neubau/anlieferung.css`. Rein
 * dekorativ (die Zahl in "Bestellt" traegt die eigentliche Information) - `Ikone` setzt bereits
 * `aria-hidden="true"` auf jedes Symbol.
 */
function LieferAmpel({ status }: { status: LieferStatus }) {
  return <Ikone name={LIEFER_STATUS_ICON[status]} className={`liefer-status-icon ${status}`} />;
}

/**
 * "27. Aug. 2026, 08:14" (Datum + eine Uhrzeit, wenn `zeitVon === zeitBis`, oder wenn NUR eine der
 * beiden Zeiten lesbar ist - SYMMETRISCH: faellt `zeitVon` aus, zeigt sich `zeitBis` und umgekehrt,
 * keine der beiden Richtungen wird bevorzugt verworfen), "27. Aug. 2026, 08:14–16:42" (Datum +
 * Spanne, wenn BEIDE Zeiten lesbar UND verschieden sind), sonst nur "27. Aug. 2026" (Ruckfall, wenn
 * GAR keine Uhrzeit lesbar ist - NIE crashen). `datum` selbst fehlend/unlesbar bleibt Sache des
 * Aufrufers (Rueckfall-Strich "—", wie bisher).
 *
 * Nachbesserung (Pruefung, Punkt 5, 2026-08-28): VORHER verwarf ein fehlendes `zeitVon` bei
 * VORHANDENEM `zeitBis` stillschweigend die vorhandene Uhrzeit (Ruckfall auf reines Datum) - ein
 * Zweig, den es fuer den umgekehrten Fall (`zeitVon` vorhanden, `zeitBis` fehlt) nicht gab.
 */
function einbuchungsZeitpunkt(e: JtlEinbuchung): string {
  const datumText = fmtDate(e.datum);
  if (!e.zeitVon && !e.zeitBis) return datumText;
  if (!e.zeitVon) return `${datumText}, ${e.zeitBis}`;
  if (!e.zeitBis || e.zeitBis === e.zeitVon) return `${datumText}, ${e.zeitVon}`;
  return `${datumText}, ${e.zeitVon}–${e.zeitBis}`;
}

/**
 * Das SPAETESTE Buchungsdatum ueber ALLE Positionen hinweg (nicht nur die letzte Position in
 * `einbuchungen` - siehe Docstring-Abschnitt oben, „Vollstaendig eingebucht"-Datum). ISO-Datumsstrings
 * ("YYYY-MM-DD") sind lexikographisch sortierbar, deshalb genuegt ein String-Vergleich ohne
 * Date-Parsing. `null`-Daten werden uebersprungen (unbekanntes Datum darf das echte spaeteste nicht
 * verdraengen). Keine Buchung mit lesbarem Datum -> `null`.
 */
function spaetestesEinbuchungsdatum(einbuchungen: EinbuchungenJePosition[]): string | null {
  let spaetestes: string | null = null;
  for (const position of einbuchungen) {
    for (const buchung of position.einbuchungen) {
      if (buchung.datum && (spaetestes === null || buchung.datum > spaetestes)) {
        spaetestes = buchung.datum;
      }
    }
  }
  return spaetestes;
}

function AnlieferungsZeile({
  position,
  einbuchungenDerPosition,
}: {
  position: OrderItem;
  einbuchungenDerPosition: JtlEinbuchung[] | undefined;
}) {
  const [offen, setOffen] = useState(false);
  const hatEinbuchungen = einbuchungenDerPosition !== undefined && einbuchungenDerPosition.length > 0;
  const bezeichnung = (position.name as string) || (position.sku as string) || "Unbenannte Position";
  const artikelnummer = (position.sku as string) || "–";

  return (
    <>
      <tr className="arrival-position-zeile">
        <td>
          <div className="arrival-position-artikel">
            {hatEinbuchungen ? (
              <button
                type="button"
                className="arrival-position-schalter"
                aria-expanded={offen}
                aria-label={`${bezeichnung} ${offen ? "einklappen" : "aufklappen"}`}
                onClick={() => setOffen((vorher) => !vorher)}
              >
                <Ikone name={offen ? "circle-minus" : "circle-plus"} />
              </button>
            ) : null}
            <Artikelbild kArtikel={position.kArtikel} name={bezeichnung} hatBild={position.hatBild} />
            <span className="arrival-position-name">{bezeichnung}</span>
          </div>
        </td>
        <td className="arrival-position-sku">{artikelnummer}</td>
        <td className="mono">{NUM.format(Number(position.qty ?? 0))}</td>
        <td className="liefer-status-spalte">
          <LieferAmpel status={lieferStatusPosition(position)} />
        </td>
        <td className="mono">{mengeText(position.mengeGeliefert)}</td>
        <td className="mono">{mengeText(position.anzahlOffen)}</td>
      </tr>
      {offen && hatEinbuchungen ? (
        <tr className="arrival-einbuchungen-zeile">
          <td colSpan={6}>
            <table className="arrival-einbuchungen-tabelle">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th className="mono">Menge</th>
                  <th>Eingebucht von</th>
                </tr>
              </thead>
              <tbody>
                {einbuchungenDerPosition.map((e, index) => (
                  <tr key={index}>
                    <td>{e.datum ? einbuchungsZeitpunkt(e) : "—"}</td>
                    <td className="mono">{NUM.format(Number(e.menge ?? 0))}</td>
                    <td>{e.benutzer ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function Anlieferungsliste({
  bestellung,
  einbuchungen,
  fehlgeschlagen,
}: AnlieferungslisteProps) {
  const items = (bestellung.items ?? []) as OrderItem[];
  const nachPosition = new Map<number, JtlEinbuchung[]>(
    (einbuchungen ?? []).map((e) => [e.kLieferantenBestellungPos, e.einbuchungen]),
  );

  if (items.length === 0) {
    return <p className="calc-note">Keine Positionen vorhanden.</p>;
  }

  const spaetestesDatum = einbuchungen !== null ? spaetestesEinbuchungsdatum(einbuchungen) : null;
  // Gesamtstatus-Banner rot/gelb (Auftrag "Lieferstatus-Icons + Uhrzeit", 2026-08-28) - Geschwister
  // des gruenen "Vollstaendig eingebucht"-Banners oben (der Fall bleibt dessen eigene Bedingung,
  // unveraendert). Nur relevant, wenn die Bestellung NICHT bereits vollstaendig geliefert ist - siehe
  // Bedingung an der Renderstelle unten. `hatGelieferteMenge` ist dieselbe Ableitung wie in
  // `lieferStatusPosition` (`lib/orderList.ts`) - hier aufgerufen statt wortgleich wiederholt
  // (Pruefung, Nachbesserung 2026-08-28, Punkt 4).
  const hatTeillieferung = items.some(hatGelieferteMenge);
  const komplettAnzahl = items.filter((item) => lieferStatusPosition(item) === "komplett").length;

  return (
    <>
      {einbuchungen === null ? (
        <p className="calc-note">
          {fehlgeschlagen
            ? "Einbuchungen konnten nicht geladen werden."
            : "Einbuchungen noch nicht geladen."}
        </p>
      ) : null}
      {vollstaendigGeliefert(bestellung) && spaetestesDatum !== null ? (
        <p className="done-banner icon-label">
          <Ikone name="circle-check-big" />
          Vollständig eingebucht am {fmtDate(spaetestesDatum)}.
        </p>
      ) : null}
      {einbuchungen !== null && !vollstaendigGeliefert(bestellung) ? (
        hatTeillieferung ? (
          <p className="done-banner icon-label liefer-banner-teilweise">
            <Ikone name="circle-dashed" />
            Teilweise eingebucht – {komplettAnzahl} von {items.length} Positionen vollständig geliefert.
          </p>
        ) : (
          <p className="done-banner icon-label liefer-banner-offen">
            <Ikone name="circle" />
            Noch keine Einbuchung erfolgt.
          </p>
        )
      ) : null}
      <table className="arrival-positionen-tabelle" data-permission="view_board">
        <thead>
          <tr>
            <th>Artikel</th>
            <th>Artikelnummer</th>
            <th className="mono">Bestellt</th>
            <th className="mono liefer-status-spalte">Status</th>
            <th className="mono">Geliefert</th>
            <th className="mono">Offen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((position, index) => (
            <AnlieferungsZeile
              key={(position.kLieferantenBestellungPos as number | undefined) ?? index}
              position={position}
              einbuchungenDerPosition={nachPosition.get(position.kLieferantenBestellungPos as number)}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}
